import {
  useState,
  useRef,
  useCallback,
  useEffect,
  useContext,
  useMemo,
} from 'react';
import { MicrophoneStreamContext } from '../../../core/src/components/MicrophoneStreamProvider/MicrophoneStreamProvider';
import { usePrevious } from './StateHooks';
import useGetDeepgramToken from '@vh/utility/useGetDeepgramToken';
import { useFeature } from '@core/components/LaunchDarkly';
import { useInterval } from '@vh/utility/TimerHooks';
import axios from 'axios';
import useDeepgramEndpoint from '@vh/utility/useDeepgramEndpoint';

/** 
 * @typedef {import('./useDeepgram.types').UseDeepgramProps} UseDeepgramProps
 * @typedef {import('./useDeepgram.types').UseDeepgramReturn} UseDeepgramReturn
 * */

/**
 * Legacy React hook that manages microphone streaming & Deepgram live transcription
 * directly with websockets
 * @type {(props: UseDeepgramProps) => UseDeepgramReturn}
 */
export default function useDeepgramDirect({ language, paused, onClose }) {
  const [currentUnfinishedAffirmation, setCurrentUnfinishedAffirmation] =
    useState('');
  const [currentAffirmation, setCurrentAffirmation] = useState('');
  const [finalAffirmation, setFinalAffirmation] = useState('');
  const socketRef = useRef(null);
  const [error, setError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const micStreamRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const noEndpointingDeepgramFeature = useFeature('no-endpointing-deepgram');
  const { stream, error: _micStreamError } = useContext(
    MicrophoneStreamContext
  );

  const audioChunksRef = useRef([]);

  const createAndStartMediaRecorder = useCallback((stream) => {
    // don't specify mimeType anymore because safari uses mp4, not webm, and deepgram can handle all
    const mediaRecorder = new MediaRecorder(stream);
    mediaRecorder.start(1000);
    return mediaRecorder;
  }, []);

  const stopMediaRecorder = useCallback((mediaRecorder) => {
    if (typeof mediaRecorder?.stop === 'function') {
      mediaRecorder.stop();
    }
  }, []);

  const finalAffirmationRef = useRef('');
  useEffect(() => {
    finalAffirmationRef.current = finalAffirmation;
  }, [finalAffirmation]);

  const sttToken = useGetDeepgramToken();

  let localeCode = useMemo(() => {
    let localeCode = language?.deepgramCode ?? language?.BCP47Code ?? 'en';
    // if english, use the local region
    if (
      localeCode?.indexOf('en') === 0 &&
      navigator.languages?.some((lang) => lang.startsWith('en'))
    ) {
      // Use the first English locale from the user's preferred languages
      localeCode =
        navigator.languages.find((lang) => lang.startsWith('en')) || 'en';
    }
    return localeCode;
  }, [language?.BCP47Code, language?.deepgramCode]);

  const deepgramEndpoint = useDeepgramEndpoint(localeCode);

  const closeSocket = useCallback(
    (code, reason, isOnCloseEvent) => {
      console.log('close socket?');
      if (mediaRecorderRef?.current) {
        stopMediaRecorder(mediaRecorderRef.current);
        mediaRecorderRef.current = null;
      }
      if (socketRef.current) {
        console.log('close socket');
        console.info({
          code,
          reason,
          readyState: socketRef.current.readyState,
        });
        if (
          typeof socketRef.current?.close === 'function' &&
          socketRef.current.readyState === 1
        ) {
          console.info({
            message: 'socketRef.current.close',
            code: code ?? 1000,
            reason,
          });
          if (socketRef.current && !isOnCloseEvent) {
            console.log('force socket to close');
            socketRef.current.close(code ?? 1000, reason);
          }
          if (onClose) {
            console.log('trigger onClose');
            onClose(code ?? 1000, reason);
          }
          setConnecting(false);
          setConnected(false);
        }
        console.log('set socketRef.current to null');
        socketRef.current = null;
      }
    },
    [onClose, stopMediaRecorder]
  );

  // connected ref to access inside callback functions
  const connectedRef = useRef(false);
  useEffect(() => {
    connectedRef.current = connected;
  }, [connected]);
  const currentAffirmationRef = useRef('');
  useEffect(() => {
    currentAffirmationRef.current = currentAffirmation;
  }, [currentAffirmation]);
  const pausedRef = useRef(false);

  useEffect(() => {
    const onOffline = (event) => {
      closeSocket(3000, 'You have lost network connection');
      console.info('offline', event);
    };

    window.addEventListener('offline', onOffline);

    return () => {
      window.removeEventListener('offline', onOffline);
    };
  }, [closeSocket]);

  // keep alive
  useInterval(() => {
    if (connected && socketRef.current) {
      const keepAliveMsg = JSON.stringify({ type: 'KeepAlive' });
      if (socketRef.current.readyState === 1) {
        socketRef.current.send(keepAliveMsg);
      }
    }
  }, 4000);

  // check connection
  useInterval(async () => {
    if (connected && socketRef.current) {
      try {
        const pingResponse = await axios.get(
          `https://robotnik.virti.com/api/health-check`,
          { timeout: 1000 }
        );
        if (pingResponse.data !== 'ok') {
          console.log('lost connection via success check');
          closeSocket(3000, 'You have lost network connection');
        }
      } catch (e) {
        console.log('lost connection via success check error');
        closeSocket(3000, 'You have lost network connection');
      }
    }
  }, 1000);

  const setupMediaRecorder = useCallback(
    (socket) => {
      if (!mediaRecorderRef.current) {
        return;
      }
      const mediaRecorder = mediaRecorderRef.current;
      mediaRecorderRef.current.addEventListener(
        'dataavailable',
        async (event) => {
          if (mediaRecorderRef.current !== mediaRecorder) {
            stopMediaRecorder(mediaRecorder);
            return;
          }
          if (event.data.size > 0 && socket.readyState === 1) {
            // we have to send this constantly or the server connection to deepgram fails
            if (!pausedRef.current) {
              socket.send(event.data);
            }
          } else {
            // this should theoretically push data that has been accumulated before
            // connection has been forged, but it may be that this never happens
            // because enough time never passes between connection and first data
            audioChunksRef.current.push(event.data);
          }
        }
      );
    },
    [stopMediaRecorder]
  );

  // use prettier-ignore beacuse it's adding a semi-colon before the dependency array...
  // prettier-ignore
  const activateMicrophone = useCallback(async () => {
    console.log('activate mic');
    // TODO: MAKE SURE IF MIC HAS BEEN ACTIVATED THEN STT TOKEN IS SET
    // (may need to put in a context?)
    if (!sttToken) {
      return;
    }

    // don't run if already active
    if (
      connectedRef.current &&
      (!!mediaRecorderRef?.current || !!socketRef?.current)
    ) {
      console.log("already active: don't run");
      return;
    }
    if (socketRef.current) {
      console.log(socketRef.current);
      return;
    }

    // set up mediarecorder now so that it can record while still connecting
    let mediaRecorder = null;
    try {
      mediaRecorder = createAndStartMediaRecorder(stream);
    } catch (e) {
      console.log('Failed to start mediarecorder: ');
      console.error(e);
      return;
    }

    mediaRecorderRef.current = mediaRecorder;
    setConnecting(true);
    micStreamRef.current = stream;

    const socket = new WebSocket(deepgramEndpoint, ['token', sttToken ?? '']);
    socketRef.current = socket;
    audioChunksRef.current = [];
    console.log('connect to:' + deepgramEndpoint);
    socket.onopen = () => {
      if (socketRef.current !== socket) {
        // if this socket is no longer in use by the time it has actually set up,
        // then we want to close it
        console.log('send CloseStream');
        socket.send(JSON.stringify({ type: 'CloseStream' }));
      }

      if (!mediaRecorderRef.current) return;
      setConnected(true);
      setupMediaRecorder(socket);
      console.log('catch up and send chunks');
      console.log(audioChunksRef.current);
      audioChunksRef.current.forEach((chunk) => socket.send(chunk));
    };

    socket.onmessage = (message) => {
      console.log({ event: 'onmessage', message });
      setConnecting(false);

      if (pausedRef.current) return;
      const received = JSON.parse(message.data);

      if (received?.error) {
        console.log('deepgram errored');
        closeSocket();
        setError(received?.error);
        // setDeepgramErrorCount((c) => c + 1);
        return;
      } else {
        setError(null);
      }
      // setDeepgramErrorCount(0);
      // console.log(received);
      const transcript = received?.channel?.alternatives?.[0]?.transcript;
      if (
        (noEndpointingDeepgramFeature && received?.type === 'UtteranceEnd') ||
        (!noEndpointingDeepgramFeature &&
          (received?.speech_final || received?.type === 'UtteranceEnd'))
      ) {
        const finalAffirmation = (
          currentAffirmationRef.current +
          (currentAffirmationRef.current.length > 0 ? ' ' : '') +
          (transcript ?? '')
        ).trim();
        console.log(received?.speech_final ? 'speech final' : 'utterance end');
        console.log(finalAffirmation);
        setCurrentUnfinishedAffirmation('');
        setCurrentAffirmation('');
        if (finalAffirmation !== '') {
          setFinalAffirmation(finalAffirmation);
        }
      } else if (received?.is_final) {
        console.log('is final');
        console.log(
          `${currentAffirmationRef.current ?? ''}${
            transcript ? (transcript.length > 0 ? ' ' : '') + transcript : ''
          }`
        );
        const newCurrentAffirmation = `${currentAffirmationRef.current ?? ''}${
          transcript ? (transcript.length > 0 ? ' ' : '') + transcript : ''
        }`.trim();
        setCurrentUnfinishedAffirmation('');
        setCurrentAffirmation(newCurrentAffirmation);
      } else if (transcript) {
        console.log('unfinished');
        console.log(transcript);
        setCurrentUnfinishedAffirmation(transcript.trim());
      }
    };

    socket.onclose = (e) => {
      console.log({ event: 'onclose', code: e.code, reason: e.reason });
      setConnecting(false);
      setConnected(false);
      if (socketRef.current === socket) {
        closeSocket(e.code, e.reason, true);
      }
    };

    socket.onerror = (error) => {
      console.log({ event: 'onerror' });
      setConnecting(false);
      console.log('socket err');
      console.log(error);
      closeSocket();
      setConnected(false);
      setError(error);
    };
  }, [
    sttToken,
    stream,
    deepgramEndpoint,
    createAndStartMediaRecorder,
    setupMediaRecorder,
    noEndpointingDeepgramFeature,
    closeSocket,
  ]);

  // pause toggled so resume or stop
  const prevPaused = usePrevious(paused);
  useEffect(() => {
    if (paused === prevPaused) {
      return;
    }
    setCurrentAffirmation('');
    setCurrentUnfinishedAffirmation('');
    setFinalAffirmation('');
    console.log(paused ? 'should pause' : 'should resume');
    if (paused) {
      closeSocket();
    }
    if (!paused && !connected) {
      activateMicrophone();
    }
    pausedRef.current = paused;
  }, [
    paused,
    prevPaused,
    closeSocket,
    activateMicrophone,
    mediaRecorderRef,
    stream,
    sttToken,
    connected,
  ]);

  const lastTranscriptTime = useRef(0);
  useEffect(() => {
    lastTranscriptTime.current = new Date().getTime();
  }, [currentUnfinishedAffirmation, currentAffirmation]);
  // ensure close socket if activateMicrophone has been run externally
  useEffect(() => {
    return () => {
      console.log('close socket via useeffect return');
      closeSocket();
    };
  }, [closeSocket]);

  const resetTranscript = useCallback(() => {
    setCurrentAffirmation('');
    setFinalAffirmation('');
    setCurrentUnfinishedAffirmation('');
  }, []);

  let currentTranscript = '';
  if (currentAffirmation) {
    currentTranscript =
      currentAffirmation +
      (currentAffirmation.length > 0 ? ' ' : '') +
      currentUnfinishedAffirmation;
  } else if (currentUnfinishedAffirmation) {
    currentTranscript = currentUnfinishedAffirmation;
  }

  return {
    currentTranscript,
    currentUnfinishedAffirmation,
    currentAffirmation,
    finalAffirmation,
    error,
    connected,
    reconnect: activateMicrophone,
    resetTranscript,
    connecting,
    closeSocket,
  };
}
